import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DashboardLayout, DashboardWidget, DashboardWidgetSize, DashboardWidgetMode, CustomDashboard } from '../types';

interface DashboardState {
  layouts: Record<string, DashboardLayout>; // userId -> layout (legacy)
  customDashboards: Record<string, CustomDashboard[]>; // userId -> dashboards
  activeDashboard: Record<string, string>; // userId -> dashboardId
  
  // Legacy methods
  getUserLayout: (userId: string) => DashboardLayout;
  setUserLayout: (userId: string, layout: DashboardLayout) => void;
  
  // New dashboard methods
  getUserDashboards: (userId: string) => CustomDashboard[];
  getActiveDashboard: (userId: string) => CustomDashboard | null;
  setActiveDashboard: (userId: string, dashboardId: string) => void;
  createDashboard: (userId: string, name: string) => CustomDashboard;
  updateDashboard: (userId: string, dashboardId: string, updates: Partial<CustomDashboard>) => void;
  deleteDashboard: (userId: string, dashboardId: string) => void;
  duplicateDashboard: (userId: string, dashboardId: string, newName: string) => CustomDashboard;
  
  // Widget methods (work with active dashboard)
  addWidget: (userId: string, roomId: string) => void;
  removeWidget: (userId: string, widgetId: string) => void;
  updateWidget: (userId: string, widgetId: string, updates: Partial<DashboardWidget>) => void;
  moveWidget: (userId: string, widgetId: string, position: { x: number; y: number }) => void;
  resizeWidget: (userId: string, widgetId: string, size: DashboardWidgetSize) => void;
  changeWidgetMode: (userId: string, widgetId: string, mode: DashboardWidgetMode) => void;
  toggleWidgetVisibility: (userId: string, widgetId: string) => void;
  toggleWidgetStateChanges: (userId: string, widgetId: string) => void;
  toggleGlobalStateChanges: (userId: string) => void;
  clearLayout: (userId: string) => void;
  autoArrangeWidgets: (userId: string) => void;
}

const DEFAULT_LAYOUT: DashboardLayout = {
  widgets: [],
  gridSize: 20,
  autoArrange: false,
  globalStateChanges: false
};

const GRID_SIZE = 20;
const MIN_SIZE = { width: 80, height: 80 };
const DEFAULT_SIZE = { width: 180, height: 180 };

// Snap to grid function
const snapToGrid = (value: number, gridSize: number = GRID_SIZE): number => {
  return Math.round(value / gridSize) * gridSize;
};

// Predefined size presets for quick selection
const SIZE_PRESETS = {
  tiny: { width: 80, height: 80 },
  small: { width: 120, height: 120 },
  medium: { width: 180, height: 180 },
  large: { width: 240, height: 240 },
  wide: { width: 280, height: 140 },
  tall: { width: 140, height: 280 },
  extraWide: { width: 360, height: 120 },
  extraTall: { width: 120, height: 360 }
};

const createDefaultDashboard = (name: string = 'Main Dashboard'): CustomDashboard => ({
  id: crypto.randomUUID(),
  name,
  widgets: [],
  gridSize: 20,
  autoArrange: false,
  globalStateChanges: false,
  createdAt: Date.now(),
  updatedAt: Date.now()
});

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set, get) => ({
      layouts: {},
      customDashboards: {},
      activeDashboard: {},

      // Legacy methods for backward compatibility
      getUserLayout: (userId: string) => {
        const state = get();
        const dashboards = state.customDashboards[userId] || [];
        const activeDashboardId = state.activeDashboard[userId];
        const activeDashboard = dashboards.find(d => d.id === activeDashboardId) || dashboards[0];
        
        if (activeDashboard) {
          return {
            widgets: activeDashboard.widgets,
            gridSize: activeDashboard.gridSize,
            autoArrange: activeDashboard.autoArrange,
            globalStateChanges: activeDashboard.globalStateChanges
          };
        }
        
        return state.layouts[userId] || DEFAULT_LAYOUT;
      },

      setUserLayout: (userId: string, layout: DashboardLayout) => {
        const state = get();
        const dashboards = state.customDashboards[userId] || [];
        const activeDashboardId = state.activeDashboard[userId];
        let activeDashboard = dashboards.find(d => d.id === activeDashboardId);
        
        if (!activeDashboard) {
          // Create default dashboard if none exists
          activeDashboard = createDefaultDashboard();
          const newDashboards = [...dashboards, activeDashboard];
          set(state => ({
            customDashboards: {
              ...state.customDashboards,
              [userId]: newDashboards
            },
            activeDashboard: {
              ...state.activeDashboard,
              [userId]: activeDashboard!.id
            }
          }));
        }
        
        // Update the active dashboard
        get().updateDashboard(userId, activeDashboard.id, {
          widgets: layout.widgets,
          gridSize: layout.gridSize,
          autoArrange: layout.autoArrange,
          globalStateChanges: layout.globalStateChanges
        });
      },

      // New dashboard methods
      getUserDashboards: (userId: string) => {
        const state = get();
        const dashboards = state.customDashboards[userId] || [];
        
        // Ensure user has at least one dashboard
        if (dashboards.length === 0) {
          const defaultDashboard = createDefaultDashboard();
          set(state => ({
            customDashboards: {
              ...state.customDashboards,
              [userId]: [defaultDashboard]
            },
            activeDashboard: {
              ...state.activeDashboard,
              [userId]: defaultDashboard.id
            }
          }));
          return [defaultDashboard];
        }
        
        return dashboards;
      },

      getActiveDashboard: (userId: string) => {
        const state = get();
        const dashboards = state.getUserDashboards(userId);
        const activeDashboardId = state.activeDashboard[userId];
        return dashboards.find(d => d.id === activeDashboardId) || dashboards[0] || null;
      },

      setActiveDashboard: (userId: string, dashboardId: string) => {
        set(state => ({
          activeDashboard: {
            ...state.activeDashboard,
            [userId]: dashboardId
          }
        }));
      },

      createDashboard: (userId: string, name: string) => {
        const newDashboard = createDefaultDashboard(name);
        
        set(state => {
          const userDashboards = state.customDashboards[userId] || [];
          return {
            customDashboards: {
              ...state.customDashboards,
              [userId]: [...userDashboards, newDashboard]
            },
            activeDashboard: {
              ...state.activeDashboard,
              [userId]: newDashboard.id
            }
          };
        });
        
        return newDashboard;
      },

      updateDashboard: (userId: string, dashboardId: string, updates: Partial<CustomDashboard>) => {
        set(state => {
          const userDashboards = state.customDashboards[userId] || [];
          const updatedDashboards = userDashboards.map(dashboard =>
            dashboard.id === dashboardId
              ? { ...dashboard, ...updates, updatedAt: Date.now() }
              : dashboard
          );
          
          return {
            customDashboards: {
              ...state.customDashboards,
              [userId]: updatedDashboards
            }
          };
        });
      },

      deleteDashboard: (userId: string, dashboardId: string) => {
        set(state => {
          const userDashboards = state.customDashboards[userId] || [];
          const filteredDashboards = userDashboards.filter(d => d.id !== dashboardId);
          
          // Ensure at least one dashboard exists
          if (filteredDashboards.length === 0) {
            const defaultDashboard = createDefaultDashboard();
            return {
              customDashboards: {
                ...state.customDashboards,
                [userId]: [defaultDashboard]
              },
              activeDashboard: {
                ...state.activeDashboard,
                [userId]: defaultDashboard.id
              }
            };
          }
          
          // If deleting active dashboard, switch to first available
          const newActiveDashboard = state.activeDashboard[userId] === dashboardId
            ? filteredDashboards[0].id
            : state.activeDashboard[userId];
          
          return {
            customDashboards: {
              ...state.customDashboards,
              [userId]: filteredDashboards
            },
            activeDashboard: {
              ...state.activeDashboard,
              [userId]: newActiveDashboard
            }
          };
        });
      },

      duplicateDashboard: (userId: string, dashboardId: string, newName: string) => {
        const state = get();
        const userDashboards = state.customDashboards[userId] || [];
        const originalDashboard = userDashboards.find(d => d.id === dashboardId);
        
        if (!originalDashboard) return createDefaultDashboard(newName);
        
        const duplicatedDashboard: CustomDashboard = {
          ...originalDashboard,
          id: crypto.randomUUID(),
          name: newName,
          widgets: originalDashboard.widgets.map(widget => ({
            ...widget,
            id: crypto.randomUUID()
          })),
          createdAt: Date.now(),
          updatedAt: Date.now()
        };
        
        set(state => ({
          customDashboards: {
            ...state.customDashboards,
            [userId]: [...userDashboards, duplicatedDashboard]
          },
          activeDashboard: {
            ...state.activeDashboard,
            [userId]: duplicatedDashboard.id
          }
        }));
        
        return duplicatedDashboard;
      },

      // Widget methods (work with active dashboard)
      addWidget: (userId: string, roomId: string) => {
        const state = get();
        const activeDashboard = state.getActiveDashboard(userId);
        if (!activeDashboard) return;
        
        // Check if widget already exists
        const existingWidget = activeDashboard.widgets.find(w => w.roomId === roomId);
        if (existingWidget) {
          // Just make it visible if it exists
          get().updateWidget(userId, existingWidget.id, { visible: true });
          return;
        }

        // Find a good position for the new widget
        const newPosition = findAvailablePosition(activeDashboard.widgets);
        
        const newWidget: DashboardWidget = {
          id: crypto.randomUUID(),
          roomId,
          position: newPosition,
          size: DEFAULT_SIZE,
          mode: 'color-text-title',
          visible: true,
          enableStateChanges: false
        };

        get().updateDashboard(userId, activeDashboard.id, {
          widgets: [...activeDashboard.widgets, newWidget]
        });
      },

      removeWidget: (userId: string, widgetId: string) => {
        const state = get();
        const activeDashboard = state.getActiveDashboard(userId);
        if (!activeDashboard) return;
        
        get().updateDashboard(userId, activeDashboard.id, {
          widgets: activeDashboard.widgets.filter(w => w.id !== widgetId)
        });
      },

      updateWidget: (userId: string, widgetId: string, updates: Partial<DashboardWidget>) => {
        const state = get();
        const activeDashboard = state.getActiveDashboard(userId);
        if (!activeDashboard) return;
        
        get().updateDashboard(userId, activeDashboard.id, {
          widgets: activeDashboard.widgets.map(w =>
            w.id === widgetId ? { ...w, ...updates } : w
          )
        });
      },

      moveWidget: (userId: string, widgetId: string, position: { x: number; y: number }) => {
        // Snap position to grid
        const snappedPosition = {
          x: snapToGrid(position.x),
          y: snapToGrid(position.y)
        };
        get().updateWidget(userId, widgetId, { position: snappedPosition });
      },

      resizeWidget: (userId: string, widgetId: string, size: DashboardWidgetSize) => {
        // Snap size to grid and enforce minimum size
        const snappedSize = {
          width: Math.max(MIN_SIZE.width, snapToGrid(size.width)),
          height: Math.max(MIN_SIZE.height, snapToGrid(size.height))
        };
        get().updateWidget(userId, widgetId, { size: snappedSize });
      },

      changeWidgetMode: (userId: string, widgetId: string, mode: DashboardWidgetMode) => {
        get().updateWidget(userId, widgetId, { mode });
      },

      toggleWidgetVisibility: (userId: string, widgetId: string) => {
        const state = get();
        const activeDashboard = state.getActiveDashboard(userId);
        if (!activeDashboard) return;
        
        const widget = activeDashboard.widgets.find(w => w.id === widgetId);
        if (widget) {
          get().updateWidget(userId, widgetId, { visible: !widget.visible });
        }
      },

      toggleWidgetStateChanges: (userId: string, widgetId: string) => {
        const state = get();
        const activeDashboard = state.getActiveDashboard(userId);
        if (!activeDashboard) return;
        
        const widget = activeDashboard.widgets.find(w => w.id === widgetId);
        if (widget) {
          get().updateWidget(userId, widgetId, { enableStateChanges: !widget.enableStateChanges });
        }
      },

      toggleGlobalStateChanges: (userId: string) => {
        const state = get();
        const activeDashboard = state.getActiveDashboard(userId);
        if (!activeDashboard) return;
        
        get().updateDashboard(userId, activeDashboard.id, {
          globalStateChanges: !activeDashboard.globalStateChanges
        });
      },

      clearLayout: (userId: string) => {
        const state = get();
        const activeDashboard = state.getActiveDashboard(userId);
        if (!activeDashboard) return;
        
        get().updateDashboard(userId, activeDashboard.id, {
          widgets: [],
          autoArrange: false,
          globalStateChanges: false
        });
      },

      autoArrangeWidgets: (userId: string) => {
        const state = get();
        const activeDashboard = state.getActiveDashboard(userId);
        if (!activeDashboard) return;
        
        const visibleWidgets = activeDashboard.widgets.filter(w => w.visible);
        
        // Arrange widgets in a grid
        const cols = Math.ceil(Math.sqrt(visibleWidgets.length));
        const spacing = GRID_SIZE;
        
        const arrangedWidgets = activeDashboard.widgets.map(widget => {
          if (!widget.visible) return widget;
          
          const visibleIndex = visibleWidgets.findIndex(w => w.id === widget.id);
          const row = Math.floor(visibleIndex / cols);
          const col = visibleIndex % cols;
          
          return {
            ...widget,
            position: {
              x: col * (DEFAULT_SIZE.width + spacing) + spacing,
              y: row * (DEFAULT_SIZE.height + spacing) + spacing
            }
          };
        });

        get().updateDashboard(userId, activeDashboard.id, {
          widgets: arrangedWidgets
        });
      }
    }),
    {
      name: 'dashboard-layouts',
      version: 4,
      migrate: (persistedState: any, version: number) => {
        // If the stored version is less than 4, reset the dashboard data
        if (version < 4) {
          return {
            layouts: {},
            customDashboards: {},
            activeDashboard: {}
          };
        }
        return persistedState;
      }
    }
  )
);

// Helper function to find available position for new widgets
function findAvailablePosition(existingWidgets: DashboardWidget[]): { x: number; y: number } {
  const spacing = GRID_SIZE;
  
  // Try to place in a grid pattern
  for (let row = 0; row < 20; row++) {
    for (let col = 0; col < 20; col++) {
      const x = col * (DEFAULT_SIZE.width + spacing) + spacing;
      const y = row * (DEFAULT_SIZE.height + spacing) + spacing;
      
      // Check if this position overlaps with any existing widget
      const overlaps = existingWidgets.some(widget => {
        if (!widget.visible) return false;
        
        const widgetRight = widget.position.x + widget.size.width;
        const widgetBottom = widget.position.y + widget.size.height;
        const newRight = x + DEFAULT_SIZE.width;
        const newBottom = y + DEFAULT_SIZE.height;
        
        return !(x >= widgetRight || newRight <= widget.position.x || 
                y >= widgetBottom || newBottom <= widget.position.y);
      });
      
      if (!overlaps) {
        return { x, y };
      }
    }
  }
  
  // Fallback to a random position
  return {
    x: snapToGrid(Math.random() * 400 + spacing),
    y: snapToGrid(Math.random() * 400 + spacing)
  };
}

export { SIZE_PRESETS, GRID_SIZE, MIN_SIZE };